import lodash from 'lodash';
import moment from 'moment';

import { DATE_FORMAT , DATE_TIME_FORMAT } from './DelegationAuditLog';

export const formatDateOnly = date => moment.utc(date).format(DATE_FORMAT);
export const formatDateTime = (date, timezoneOffset) =>  moment(date).utcOffset(timezoneOffset).format(DATE_TIME_FORMAT);

const startDate = (startDate) => startDate ? `Start Date: ${formatDateOnly(startDate)}` : '';
const endDate = (endDate) => endDate ? `End Date: ${formatDateOnly(endDate)}` : '';

/**
 * @typedef {import('./DelegationAuditLog').DOA} DOA
 */


/**
 * Default handler formatting of study audit log events.
 * @returns {String} A blank audit log entry.
 */
const defaultStudyAuditEventHandler = () => '';


/**
 * Handles formatting of study role audit log events.
 * @param {Object} auditLogEntry 
 * @returns {String} A formatted audit log entry.
 */
const studyRoleAuditEventHandler = auditLogEntry => {
    const role = JSON.parse(auditLogEntry.value).name;
    return role.concat(formatAuditNote(auditLogEntry.note));
};


/**
 * Handles formatting of study task audit log events.
 * @param {Object} auditLogEntry 
 * @returns {String} A formatted audit log entry.
 */
const studyTaskAuditEventHandler = auditLogEntry => {
    const task = JSON.parse(auditLogEntry.value).taskName;
    return task.concat(formatAuditNote(auditLogEntry.note));
};


/**
 * Handles formatting of delegation audit log events.
 * @param {Object} auditLogEntry 
 * @param {DOA} DOA 
 * @returns {String} A formatted audit log entry.
 */
const delegationAuditEventHandler = (auditLogEntry, DOA) => {
    const auditDelegation = JSON.parse(auditLogEntry.value);
    const delegationUserDisplayName = lodash.get(DOA, `auditLogUsers.${auditDelegation.userId}.displayName`);
    const delegationDetails = formatDelegationDetails(delegationUserDisplayName, auditDelegation, DOA.studyTasks);
    return delegationDetails.concat(formatAuditNote(auditLogEntry.note));
};


/**
 * Handles formatting of signature audit log events.
 * @param {Object} auditLogEntry 
 * @param {DOA} DOA 
 * @param {Number} auditLogIndex The index of the audit log entry.
 * @returns {String} A formatted audit log entry.
 */
const signatureAuditEventHandler = (auditLogEntry, DOA, auditLogIndex, timezoneOffset) => {
    const auditSignature = JSON.parse(auditLogEntry.value);
    const auditLogEntryOfDelegationSigned = getMostRecentVersionOfAssetRelativeToAuditLogIndex(DOA.auditLogEntries, auditSignature.delegationId, auditLogIndex);
    const auditDelegation = JSON.parse(auditLogEntryOfDelegationSigned.value);

    const delegationSignature = lodash.get(DOA, `studySignatures.${auditSignature.signatureId}`);
    const signatureManifestation = formatElectronicSignature(delegationSignature.signature, delegationSignature.signatureType, timezoneOffset);

    const delegationUserDisplayName = lodash.get(DOA, `auditLogUsers.${auditDelegation.userId}.displayName`);
    const delegationDetails = formatDelegationDetails(delegationUserDisplayName, auditDelegation, DOA.studyTasks);
    return delegationDetails.concat(signatureManifestation).concat(formatAuditNote(auditLogEntry.note));
};


/**
 * 
 * @param {String} delegationUserDisplayName The delegated user's display name.
 * @param {Object} delegation A delegation object.
 * @param {Object} studyTasks DOA study tasks object keyed by id.
 * @returns {String} Returns formatted delegation details
 */
const formatDelegationDetails = (delegationUserDisplayName, delegation, studyTasks) => {
    const tasks = lodash.map(delegation.taskIds, (taskId) => studyTasks[taskId].taskIndex);
    return `Delegation: ${delegationUserDisplayName}, ${delegation.role}, Tasks: ${tasks}, ${startDate(delegation.startDate)} ${endDate(delegation.endDate)}`;
};


/**
 * 
 * @param {Object} signature 
 * @param {String} signatureType 
 * @param {String} timezoneOffset
 * @returns {String} Returns formatted signature manifestation
 */
const formatElectronicSignature = (signature, signatureType, timezoneOffset) => {
    const signingUser = signature.user;
    const signatureTypePrefix = signatureType.split('_')[0];
    return `
Signature Manifestation: ${signingUser.displayName}, ${signingUser.emailAddress}, ${formatDateTime(signature.createdAt, timezoneOffset)}
Testament: (See ${signatureTypePrefix} Signature Testament)`;
};


/**
 * 
 * @param {String} note Audit log note
 */
const formatAuditNote = note => {
    return note ? `
Note: ${note}`: '';
};


/**
 * Finds latest asset data relative to the audit log index.
 * @param {Array<Object>} auditLogEntries An array of audit log entries.
 * @param {String} assetId Id of asset related to an audit log event.
 * @param {Number} index Index of audit log entry to start search at.
 */
const getMostRecentVersionOfAssetRelativeToAuditLogIndex = (auditLogEntries, assetId, index) => {
    return lodash.find(auditLogEntries, {assetId}, index);
};


/**
 * All DOA audit event action types and corresponding handlers.
 */
const studyAuditLogTemplates = {
    DEFAULT: defaultStudyAuditEventHandler,
    CREATE_ROLE: studyRoleAuditEventHandler,
    CREATE_STUDY_TASK: studyTaskAuditEventHandler,
    REMOVE_STUDY_TASK: studyTaskAuditEventHandler,
    IMPORT_STUDY_TASK_FROM_TEMPLATE: studyTaskAuditEventHandler,
    CREATE_DELEGATION: delegationAuditEventHandler,
    EDIT_DELEGATION: delegationAuditEventHandler,
    REMOVE_DELEGATION: delegationAuditEventHandler,
    SIGN_DELEGATION_START_DATE_AS_PI: signatureAuditEventHandler,
    SIGN_DELEGATION_START_DATE_AS_STAFF: signatureAuditEventHandler,
    SIGN_DELEGATION_END_DATE_AS_PI: signatureAuditEventHandler,
};

export default studyAuditLogTemplates;